import { useMemo } from 'react';

import { VFlex } from '@/components/base';
import Button from '@/components/Button/Button';
import ContentPreview from '@/components/Preview/ContentPreview';
import Typo from '@/components/Typography/Typo';
import type { FrontSentimentData } from '@/features/FrontPlugin/PluginFeedbackChart';
import useColor from '@/hooks/domain/useColor';
import { getFixedPosition } from '@/hooks/pure/useFixed';
import lightModeColor from '@/styles/colors/color';
import { sentimentMap } from '@/types/feedbackConst';

type Props = {
  data: FrontSentimentData;
  element: HTMLDivElement;
};

const PluginFeedbackPreview = (props: Props) => {
  const color = useColor();

  const contentPreviewStyle = useMemo(() => {
    const { width } = props.element.getBoundingClientRect();

    const pos = getFixedPosition(props.element, {
      offset: {
        y: 5,
      },
      reflect: {
        x: false,
        y: true,
      },
    });

    return {
      ...pos,
      width,
      maxHeight: 400,
      border: `1px solid ${color.gray400}`,
      borderRadius: 8,
      backgroundColor: color.gray100,
      padding: '14px 16px',
    };
  }, [color.gray100, color.gray400, props.element]);

  return (
    <ContentPreview
      style={{
        pointerEvents: 'auto',
        overflowY: 'auto',
        ...contentPreviewStyle,
      }}
    >
      <VFlex g={20}>
        {props.data.sentences.map((d, index) => {
          return (
            <VFlex g={8} key={index}>
              <Typo type="body2">{d.text}</Typo>
              <Button
                leftIcon={sentimentMap[d.sentiment].icon}
                variant="outlined"
                style={{
                  width: 'fit-content',
                  color: lightModeColor.sentiment[d.sentiment],
                  borderColor: lightModeColor.sentiment[d.sentiment],
                }}
              >
                {sentimentMap[d.sentiment].value}
              </Button>
            </VFlex>
          );
        })}
      </VFlex>
    </ContentPreview>
  );
};

export default PluginFeedbackPreview;
